import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  as?: any;
  to?: string;
}

const StyledButton = styled.button.withConfig({
  shouldForwardProp: (prop: PropertyKey) => prop !== 'fullWidth',
})<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: ${theme.typography.fontFamily.primary};
  font-weight: ${theme.typography.fontWeight.semibold};
  border-radius: ${theme.borderRadius.xl};
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
  position: relative;
  overflow: hidden;
  text-decoration: none;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    transform: translateY(-2px);
  }

  &:not(:disabled):active {
    transform: translateY(0);
  }

  ${props => props.fullWidth && `
    width: 100%;
  `}

  /* Sizes */
  ${props => {
    switch (props.size) {
      case 'sm':
        return `
          padding: 0.5rem 1rem;
          font-size: ${theme.typography.fontSize.sm};
        `;
      case 'lg':
        return `
          padding: 1rem 2rem;
          font-size: ${theme.typography.fontSize.lg};
        `;
      default:
        return `
          padding: 0.75rem 1.5rem;
          font-size: ${theme.typography.fontSize.base};
        `;
    }
  }}

  /* Variants */
  ${props => {
    switch (props.variant) {
      case 'secondary':
        return `
          background: ${theme.colors.glow.mint};
          color: ${theme.colors.text.mint};
          box-shadow: ${theme.shadows.sm};

          &:not(:disabled):hover {
            background: ${theme.colors.glow.sage};
            box-shadow: ${theme.shadows.md};
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: ${theme.colors.primary[600]};
          border: 2px solid ${theme.colors.primary[500]};

          &:not(:disabled):hover {
            background: ${theme.colors.glow.mint};
            border-color: ${theme.colors.primary[600]};
          }
        `;
      case 'ghost':
        return `
          background: transparent;
          color: ${theme.colors.text.secondary};

          &:not(:disabled):hover {
            background: ${theme.colors.glow.mint};
            color: ${theme.colors.text.mint};
          }
        `;
      case 'destructive':
        return `
          background: ${theme.colors.status.error};
          color: #ffffff;
          box-shadow: ${theme.shadows.sm};

          &:not(:disabled):hover {
            background: ${theme.colors.status.error};
            box-shadow: ${theme.shadows.md};
          }
        `;
      default:
        return `
          background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%);
          color: white;
          box-shadow: ${theme.shadows.md};

          &:not(:disabled):hover {
            background: linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.primary[700]} 100%);
            box-shadow: ${theme.shadows.lg};
          }
        `;
    }
  }}
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const Button: React.FC<ButtonProps> = (props) => {
  const {
    children,
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    loading = false,
    disabled,
    ...rest
  } = props;

  return (
    <StyledButton
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      {...rest}
    >
      {loading && <Spinner />}
      {children}
    </StyledButton>
  );
};
